import React, {useState} from 'react';
import {
    TextField,
    Button,
    Paper,
    Typography,
    Stack,
    Box,
} from '@mui/material';
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useNotification} from "../../utils/hooks/useNotification";
import {userAccountCreateSchema, UserAccountCreateSchema} from "../../schemas/user/userAccountCreateSchemas";
import {postFetcher} from "../../utils/api/fetchers";
import {useNavigate} from "react-router-dom";
import RolesSelector from "../../components/input/RolesSelector";
import UserRole, {roleLabels} from "../../schemas/user/roles";
import {UserAccount} from "../../schemas/user/userAccountSchemas";

const UserCreate: React.FC = () => {
        const navigate = useNavigate();
        const {showNotification} = useNotification();
        const {register, setValue, getValues, handleSubmit, control, formState: {errors}} = useForm<UserAccountCreateSchema>({
            resolver: zodResolver(userAccountCreateSchema),
        });
        const [roles, setRoles] = useState<UserRole[]>([UserRole.USER]);

        const removeDiacritics = (str: string) => {
            return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '').toLowerCase();
        };

        const handleGenerateUsername = () => {
            const firstName = getValues('firstName');
            const lastName = getValues('lastName');

            if (!firstName || !lastName) {
                showNotification("Vyplňte nejdříve jméno a příjmení", "error");
                return;
            }

            const randomNumber = Math.floor(Math.random() * 9); // add 1 number
            const username = `${removeDiacritics(lastName).slice(0,4)}${removeDiacritics(firstName).slice(0, 3)}${randomNumber}`;

            setValue('username', username, { shouldDirty: true });
            showNotification("Uživatelské jméno bylo vygenerováno", "success");
        }

        const addRolesToUser = (userId: number, roles: UserRole[]) => {
            const rolesToSet = roles.filter((role) => role !== UserRole.USER);
            const rolesMap = new Map<UserRole, boolean>();
            rolesToSet.forEach((role) => {
                postFetcher(`/users/${userId}/roles`, {role})
                    .then(() => {
                        rolesMap.set(role, true);
                    })
                    .catch(() => {
                        rolesMap.set(role, false);
                    });
            });

            if (Array.from(rolesMap.values()).every((value) => value)) {
                showNotification("Role byly úspěšně přidány", "success");
            } else {
                const failedRoles = rolesToSet.filter((role) => !rolesMap.get(role));
                const failedRolesLabels = failedRoles.map((role) => roleLabels[role]);
                showNotification(`Nepodařilo se přidat role: ${failedRolesLabels.join(", ")}`, "error");
            }
        }

        const onSubmit = async (data: UserAccountCreateSchema) => {
            postFetcher<UserAccountCreateSchema, UserAccount>("/users", data)
                .then((res) => {
                    showNotification("Uživatel byl úspěšně vytvořen", "success");
                    addRolesToUser(res.id, roles);
                    navigate('/users');
                })
                .catch((error) => {
                    if (error.response?.status === 409) {
                        showNotification("Uživatelské jméno nebo email již existuje", "error");
                    } else
                        showNotification("Nepodařilo se vytvořit uživatele", "error");
                });
        };

        return (
            <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
                <Typography variant="h4" gutterBottom>
                    Vytvoření uživatele
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={3}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="Jméno"
                                variant="standard"
                                required
                                {...register('firstName')}
                                error={!!errors.firstName}
                                helperText={errors.firstName?.message}
                            />
                            <TextField
                                fullWidth
                                label="Příjmení"
                                variant="standard"
                                required
                                {...register('lastName')}
                                error={!!errors.lastName}
                                helperText={errors.lastName?.message}
                            />
                        </Stack>
                        <Stack direction='row' spacing={1} alignItems='baseline'>
                            <Controller
                                name="username"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Uživatelské jméno"
                                        variant="standard"
                                        required
                                        error={!!errors.username}
                                        helperText={errors.username?.message}
                                    />
                                )}
                            />
                            <Button variant="outlined" title="Vygenerovat na základě jména a příjmení"
                                    onClick={handleGenerateUsername} tabIndex={-1}
                                    sx={{height:30}}
                            >
                                Vygenerovat
                            </Button>
                        </Stack>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                variant="standard"
                                required
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                            <TextField
                                fullWidth
                                label="Telefon"
                                variant="standard"
                                {...register('phoneNumber')}
                                error={!!errors.phoneNumber}
                                helperText={errors.phoneNumber?.message}
                            />
                        </Stack>
                        <Typography variant="body1" gutterBottom>
                            <RolesSelector selectedRoles={roles} setRoles={setRoles} confirmation={false}/>
                        </Typography>
                        <Button type="submit" variant="contained" color="primary" fullWidth>
                            Vytvořit uživatele
                        </Button>
                    </Stack>
                </Box>
            </Paper>
        );
    }
;

export default UserCreate;
